import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Table, Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { formatDistanceToNow } from "date-fns";
import { horizon } from "../api/horizon";
import { usePageMetadata } from "../context/PageMetadataContext";
import CopyIcon from "../components/CopyIcon";
import OperationText from "../components/OperationText";
import { okx } from "../api/okx";
import AccountLabel from "../components/AccountLabel";
import { isPiCoreTeamAccount } from "../utils/accountUtils";
import { getUnlockTime } from "../utils/predicate";
import { useLanguage } from "../context/LanguageContext";
import { formatDistanceToNowWithLocale } from "../utils/time";
import { CardContent } from "../components/Card";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

const TransactionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [txData, setTxData] = useState<any>(null);
  const [operations, setOperations] = useState<any[]>([]);
  const [effects, setEffects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [piPriceUSD, setPiPriceUSD] = useState(0.0);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  const fetchPiPrice = async () => {
    try {
      okx.getMarketData().then(marketData => {
        // Market Stats
        setPiPriceUSD(parseFloat(marketData.idxPx));
      })
    } catch (error) {
      console.error("Error fetching Pi price:", error);
    }
  };
  
  useEffect(() => {
    fetchPiPrice();
  }, []);
  
  useEffect(() => {
    setTitle(t('transaction.title', { id }));
    setHeading(t('transaction.heading'));
    setDescription(t('transaction.description', { id }));
    
    // Update the page metadata immediately for SEO
    document.title = t('transaction.title', { id });
    document.querySelector('meta[name="description"]')?.setAttribute("content", t('transaction.description', { id }));
  }, [id, setTitle, setDescription, setHeading, t, language]);

  // Fetch transaction detail from API
  const fetchTransactionDetail = async () => {
    try {
      setLoading(true);
      if (id) {
        const response = await horizon.getTransactionByHash(id);
        setTxData(response);
      } else {
        setTxData(null);
      }
    } catch (error) {
      console.error("Error fetching tx detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOperations = async () => {
    try {
      if (!id) {
        return;
      }
      setTabLoading(true);
      const response = await horizon.getOperationByTxHash(id);
      setOperations(response._embedded.records);
    } catch (error) {
      console.error("Error fetching operations:", error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchEffects = async () => {
    try {
      if (!id) {
        return;
      }
      setTabLoading(true);
      const response = await horizon.getEffectByTxHash(id);
      setEffects(response._embedded.records);
    } catch (error) {
      console.error("Error fetching effects:", error);
    } finally {
      setTabLoading(false);
    }
  };

  // Fetch transactions, operations, effects
  const fetchTabData = async () => {
    try {
      setTabLoading(true);
      if (id) {
        fetchOperations();
        fetchEffects();
      }
    } catch (error) {
      console.error("Error fetching tab data:", error);
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactionDetail();
    fetchTabData();
  }, [id]);

  // Format number according to user locale
  const formatNumber = (value: number): string => {
    return value.toLocaleString(language, {  
      minimumFractionDigits: 0,  
      maximumFractionDigits: 3 
    });
  };
  
  // Format currency according to user locale
  const formatCurrency = (value: number, currencyCode: string = 'USD'): string => {
    return value.toLocaleString(language, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    });
  };

  // Function to generate transaction summary
  const getTransactionSummary = () => {
    if (!operations || operations.length === 0) {
      return t('transaction.loading_details');
    }

    // Case 1: If there's only 1 operation
    if (operations.length === 1) {
      // Return a simplified version of the operation text
      return <OperationText op={operations[0]} PiPrice={piPriceUSD} />;
    }

    // Case 2: Check for claim_claimable_balance operations from Pi Core Team
    const claimOps = operations.filter(op => op.type === "create_claimable_balance");
    const isPiCoreTeam = txData?.source_account && isPiCoreTeamAccount(txData.source_account);
    if (claimOps.length > 0 && isPiCoreTeam) {
      // Count each operation as a separate recipient
      const recipientCount = claimOps.length;

      // Calculate total amount
      let totalAmount = 0;
      claimOps.forEach(op => {
        if (op.amount) {
          totalAmount += parseFloat(op.amount);
        }
      });

      // Extract lock period information
      let lockPeriodText = "";
      let minLockDays = Number.MAX_SAFE_INTEGER;
      let maxLockDays = 0;
      let hasLockPeriod = false;

      // Examine each operation to find the range of lock periods
      claimOps.forEach(op => {
        if (op.claimants && op.claimants.length > 0) {
          // Find the claimant that is not the Pi Core Team address
          const PI_CORE_ADDRESS = "GC5RNDCRO6DDM7NZDEMW3RIN5K6AHN6GMWSZ5SAH2TRJLVGQMB2I3BNJ";
          const userClaimant = op.claimants.find((c: { destination: string; }) => c.destination !== PI_CORE_ADDRESS);
          
          if (userClaimant?.predicate) {
            const predicate = userClaimant.predicate;
            let lockDays = 0;
            
            // Calculate lock days using the "not.abs_before" pattern
            if (predicate.not?.abs_before) {
              const lockEndDate = new Date(predicate.not.abs_before);
              const lockStartDate = new Date(txData.created_at || new Date());
              lockDays = Math.round((lockEndDate.getTime() - lockStartDate.getTime()) / (1000 * 60 * 60 * 24));
            } 
            // Handle relative time locks using "not.rel_before" pattern
            else if (predicate.not?.rel_before) {
              // rel_before is in seconds, convert to days
              lockDays = Math.round(predicate.not.rel_before / 86400);
            }
            // Use the getUnlockTime function as a fallback for more complex predicates
            else {
              const unlockTimeSeconds = getUnlockTime(predicate, txData.created_at);
              lockDays = Math.round(unlockTimeSeconds / (24 * 60 * 60));
            }
            
            if (lockDays > 0) {
              hasLockPeriod = true;
              minLockDays = Math.min(minLockDays, lockDays);
              maxLockDays = Math.max(maxLockDays, lockDays);
            }
          }
        }
      });

      // Format lock period text based on what we found
      if (hasLockPeriod) {
        if (minLockDays === maxLockDays) {
          // If all locks are for the same duration
          lockPeriodText = t('transaction.locked_for_days', { days: formatNumber(minLockDays) });
        } else {
          // Show the range of lock periods
          lockPeriodText = t('transaction.locked_range_days', { 
            minDays: formatNumber(minLockDays),
            maxDays: formatNumber(maxLockDays)
          });
        }
      }

      // Calculate USD value
      const usdValue = totalAmount * piPriceUSD;

      return (
        <span>
          {t('transaction.core_team_distributed')}{" "}
          <b className="pi-amount-text-success">
            {formatNumber(totalAmount)} π
          </b>{" "}
          <span className="text-muted">
            ({formatCurrency(usdValue)})
          </span> {t('transaction.to')}{" "}
          <b>
            {recipientCount}
          </b>{" "}
          {t(recipientCount === 1 ? 'transaction.account' : 'transaction.accounts')}
          {lockPeriodText}
        </span>
      );
    }

    // Default case: Generic summary with operation count
    return t('transaction.with_operations', { count: operations.length });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p>{t('transaction.loading')}</p>
      </div>
    );
  }

  if (!txData) {
    return <p className="text-center text-danger">{t('transaction.not_found')}</p>;
  }

  return (
    <div>
      <div className="white-zone">
        <h2 className="mb-4">{t('transaction.heading')}</h2>

        {/* Transaction Information */}
        <Table hover responsive borderless>
          <tbody>
            <tr>
              <th>{t('transaction.hash')}</th>
              <td className="long-text">{txData.hash} {" "}<CopyIcon textToCopy={txData.hash} /></td>
            </tr>

            <tr>
              <th>{t('transaction.ledger_block')}</th>
              <td className="long-text">
                <Link to={`/block/${txData.ledger}`} className="text-decoration-none">
                  {txData.ledger}
                </Link>
                {" "}
                <CopyIcon textToCopy={txData.ledger} />
                <span className="text-muted"> ({formatDistanceToNowWithLocale(new Date(txData.created_at), language)})</span>
              </td>
            </tr>
            <tr>
              <th>{t('transaction.result')}</th>
              <td>
                {txData.successful ? (
                  <span className="badge bg-success " style={{ fontSize: "12px" }}>
                    <i className="bi bi-check"></i> {t('transaction.successful')}
                  </span>
                ) : (
                  <span className="badge bg-danger">
                    <i className="bi bi-exclamation"></i> {t('transaction.failed')}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>{t('transaction.source_account')}</th>
              <td className="long-text">
                <AccountLabel account={txData.source_account} shorten={false} />
              </td>
            </tr>
            <tr>
              <th>{t('transaction.fee_account')}</th>
              <td className="long-text">
                <AccountLabel account={txData.fee_account} shorten={false} />
              </td>
            </tr>

            <tr>
              <th>{t('transaction.fee')}</th>
              <td>
                <b className="pi-amount-text-success">{formatNumber(parseInt(txData.fee_charged) / 10_000_000)} π</b>
              </td>
            </tr>
            <tr>
              <th>{t('transaction.operation_count')}</th>
              <td>
                {txData.operation_count}
              </td>
            </tr>
            <tr>
              <th>{t('transaction.summary')}</th>
              <td className="word-break">{getTransactionSummary()}</td>
            </tr>
          </tbody>
        </Table>
      </div>
      <div className="row g-3 mb-4  d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
      <div className="white-zone">
        <div className="mt-4">
          <h5>{t('transaction.operations')}</h5>
          <div>
            {tabLoading ? (
              <Spinner animation="border" variant="primary" className="mt-3" />
            ) : (
              <>
                <Table hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>{t('transaction.source_account')}</th>
                      <th>{t('transaction.operation')}</th>
                      <th>{t('transaction.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.map((op) => (
                      <tr key={op.id}>
                        <td>
                          <AccountLabel account={op.source_account} shorten={true} />
                        </td>
                        <td><OperationText op={op} PiPrice={piPriceUSD} /></td>
                        <td style={{ fontWeight: "500" }}>
                          {op.amount ? formatNumber(parseFloat(op.amount)) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="white-zone">
        <div className="mt-4">
          <h5>{t('transaction.effects')}</h5>
          <div>
            {tabLoading ? (
              <Spinner animation="border" variant="primary" className="mt-3" />
            ) : (
              <>
                <Table hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>{t('transaction.account')}</th>
                      <th>{t('transaction.effect')}</th>
                      <th>{t('transaction.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effects.map((effect) => (
                      <tr key={effect.id}>
                        <td>
                          <AccountLabel account={effect.account} shorten={true} />
                        </td>
                        <td>{t(`effects.${effect.type}`, effect.type.split("_").join(" "))}</td>
                        <td style={{ fontWeight: "500" }}>
                          <span className={effect.type == "account_credited" ? "text-success" : effect.type == "account_debited" ? "text-danger" : "text-muted"}>
                            {effect.type == "account_debited" ? "-" : effect.type == "account_credited" ? "+" : ""}
                            {effect.amount ? formatNumber(parseFloat(effect.amount)) : ""}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetail;
